import { ReactElement, useCallback, useEffect, useState, useRef } from "react"
import { FetchBaseQueryError } from "@reduxjs/toolkit/query"
import "react-dates/initialize"
import { FormLabel, Loading, Button, InlineLoading } from "@wfp/ui"
import { Moment } from "moment"
import ExcelUploader from "components/Shared/ExcelUploader/ExcelUploader"
import { useNavigate } from "react-router-dom"
import useCommon from "hooks/useCommon"
import { FormFetchData, SurveyMetaData } from "interfaces/dataInterfaces"
import { useSaveSurveyHHMutation, useSaveSurveyHHFetchMutation } from "services/household/survey"
import Select from "react-select"
import { SurveyMode } from "constants/household"
import { useGetDataProviderPlatformsQuery } from "services/core/user"
import SurveyFormFetch from "./SurveyFormFetch"
import SurveyMeta from "./SurveyMeta"

export const initialFormFetch: FormFetchData = {
  apiKey: 0,
  formId: 0,
  projectId: 0,
  frequencyId: 0,
  endDate: null,
}

const MAX_CSV_SIZE_BYTES = 100 * 1024 * 1024

const AddSurvey = (): ReactElement => {
  const navigate = useNavigate()
  const { showSnackBar } = useCommon()
  const reportRef = useRef<HTMLDivElement>(null)

  const [adm0code, setAdm0code] = useState<number | undefined>(undefined)
  const [xlsFormId, setXlsFormId] = useState<number | undefined>(undefined)
  const [startDate, setStartDate] = useState<Moment | null>(null)
  const [endDate, setEndDate] = useState<Moment | null>(null)
  const [file, setFile] = useState<File | undefined>(undefined)
  const [values, setValues] = useState<SurveyMetaData>({
    surveyAttributeIds: [],
    organizationIds: [],
    surveyName: "",
    surveySubCategoryId: 0,
    surveyCategoryId: 0,
    surveyModalityId: 0,
    surveyAssessmentStatusId: 0,
    surveyPhaseId: 0,
    surveyHouseholdIsContinuousMonitoring: false,
  })
  const [currentMode, setCurrentMode] = useState<number>(0)
  const [formFetch, setFormFetch] = useState<FormFetchData>(initialFormFetch)
  const [fetchingMode, setFetchingMode] = useState<number>(1)
  const [reportCsv, setReportCsv] = useState<string>("")

  const { data: platforms, isLoading: loadingPlatforms } = useGetDataProviderPlatformsQuery()
  const [saveSurvey, { isLoading: isSaving }] = useSaveSurveyHHMutation()
  const [saveSurveyFetch, { isLoading: isLoadingSaveFetch }] = useSaveSurveyHHFetchMutation()

  const handleFileRejected = (code: string): void => {
    if (code === "file-too-large") {
      showSnackBar(`CSV file is too large. Maximum allowed size is ${MAX_CSV_SIZE_BYTES / (1024 * 1024)}MB`, "error")
      return
    }
    showSnackBar("Invalid file. Please select a valid CSV file.", "error")
  }

  const validateRequiredFields = useCallback(() => {
    if (!values.surveyName || values.surveyName?.length > 50) {
      showSnackBar("Survey name field is required and must be less than 50 characters.", "error")
      return false
    }
    if (!values.surveyCategoryId) {
      showSnackBar("Category of the survey is required.")
      return false
    }
    if (!values.surveyModalityId) {
      showSnackBar("Modality of the survey is required.")
      return false
    }
    if (!values.surveyAssessmentStatusId) {
      showSnackBar("Status of the survey is required.")
      return false
    }
    if (!values.surveyPhaseId && values.surveyAssessmentStatusId !== 2) {
      showSnackBar("Phase of the survey is required.")
      return false
    }
    if (!values.surveySubCategoryId) {
      showSnackBar("Type of the survey is required.")
      return false
    }
    if (!adm0code || !xlsFormId) {
      showSnackBar(`${!adm0code ? "Country" : "XlsForm"} field is required.`, "error")
      return false
    }
    if (!startDate) {
      showSnackBar(`Start date is required.`, "error")
      return false
    }
    return true
  }, [values, adm0code, xlsFormId, startDate])

  const buildFormData = (): FormData => {
    const surveyHouseholdDetailDto = { ...values }
    const formData = new FormData()

    formData.append("xlsFormId", xlsFormId?.toString() || "")
    formData.append("adm0Code", adm0code?.toString() || "")
    formData.append("surveyStartDate", startDate?.toISOString() || "")
    formData.append("surveyEndDate", endDate?.toISOString() || "")
    formData.append("isImportByFetch", "false")

    Object.keys(surveyHouseholdDetailDto).forEach(key => {
      if (key === "organizationIds" || key === "surveyAttributeIds") {
        surveyHouseholdDetailDto[key].forEach((id: number) => {
          formData.append(`${key}`, id.toString())
        })
        return
      }
      formData.append(`${key}`, surveyHouseholdDetailDto[key] ?? "")
    })

    return formData
  }

  const handleSave = async (): Promise<void> => {
    if (!validateRequiredFields()) return
    if (!file) return showSnackBar("Please select a csv file", "warning")
    setReportCsv("")

    try {
      const formData = buildFormData()
      file && formData.append("surveyOriginalFilename", file.name)
      file && formData.append("file", file)

      const response = await saveSurvey(formData).unwrap()

      if (!response?.isSuccess) {
        setReportCsv(response?.resultMessage)
        setFile(undefined)
        window.scrollTo({ top: reportRef.current?.offsetTop, behavior: "smooth" })
        return
      }

      showSnackBar("Survey saved successfully", "success")
      navigate("/household/surveys")
    } catch (error) {
      const err = error as FetchBaseQueryError
      showSnackBar(
        (err?.data as { message: string })?.message ??
          "Upload process failed. The CSV file contains invalid or inconsistent data.\nPossible causes include empty rows, missing required fields, malformed values, or mismatched column counts.\nPlease validate the CSV structure and ensure the file matches the expected format before uploading again.",
        "error",
      )
    }
  }

  const handleSaveFetch = async (): Promise<void> => {
    if (!validateRequiredFields()) return
    try {
      const response = await saveSurveyFetch({
        adm0code: adm0code ?? 0,
        surveyEndDate: endDate?.toISOString() ?? null,
        surveyStartDate: startDate?.toISOString() ?? null,
        xlsFormId: xlsFormId ?? 0,
        isImportByFetch: true,
        ...formFetch,
        launchAndForget: fetchingMode === 1,
        ...values,
      }).unwrap()

      if (response?.isSuccess) {
        showSnackBar(response?.resultMessage, "success")
      }
      if (!response?.isSuccess) {
        showSnackBar(response?.resultMessage, "warning")
      }
      navigate("/household/surveys")
    } catch (error) {
      showSnackBar("An error occurred while saving survey, please try again later", "error")
    }
  }

  useEffect(() => {
    if (currentMode) setFormFetch(initialFormFetch)
  }, [currentMode])

  if (isLoadingSaveFetch) {
    return <Loading small />
  }

  return (
    <div className="relative">
      <SurveyMeta
        values={values}
        adm0code={adm0code}
        xlsFormId={xlsFormId}
        startDate={startDate}
        endDate={endDate}
        setValues={setValues}
        setAdm0code={setAdm0code}
        setXlsFormId={setXlsFormId}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
      />

      <div className="grid grid-cols-2 gap-4 mt-2">
        <div>
          <FormLabel htmlFor="mode" className="text-xl">
            Data import
          </FormLabel>
          <Select
            isLoading={loadingPlatforms}
            inputId="mode"
            placeholder="Select a data import modality"
            options={[{ value: 5, name: "CSV" }, ...(platforms || [])]}
            onChange={e => setCurrentMode(Number(e?.value))}
            getOptionLabel={option => option.name}
            getOptionValue={option => option.value?.toString()}
            value={[{ value: 5, name: "CSV" }, ...(platforms || [])]?.find(p => p.value === currentMode)}
          />
        </div>
      </div>

      {currentMode === SurveyMode.CSV && (
        <>
          <ExcelUploader
            accept={{ "text/csv": [".csv"] }}
            onSelect={setFile}
            maxSizeBytes={MAX_CSV_SIZE_BYTES}
            onFileRejected={handleFileRejected}
            beforeSelect={validateRequiredFields}
            placeholder="Select the CSV containing the survey responses"
          />

          <Button
            className="!flex mr-0 mt-2 m-auto"
            onClick={handleSave}
            disabled={isSaving}
            kind={isSaving ? "secondary" : "primary"}
          >
            {isSaving ? <InlineLoading /> : "Save"}
          </Button>

          {reportCsv && (
            <div className="my-2" ref={reportRef}>
              <FormLabel className="text-xl">Report</FormLabel>
              <p className="text-justify">{reportCsv}</p>
            </div>
          )}
        </>
      )}

      {currentMode && currentMode !== SurveyMode.CSV ? (
        <SurveyFormFetch
          data={formFetch}
          setData={setFormFetch}
          fetchingMode={fetchingMode}
          setFetchingMode={setFetchingMode}
          onSubmit={handleSaveFetch}
          currentMode={currentMode}
        />
      ) : null}
    </div>
  )
}

export default AddSurvey
